import { track } from "@/features/analytics";
import { useGetMediaFoldersQuery } from "@api/media-folders";
import { useExtractWebsiteMediaMutation } from "@api/media-library";
import Button from "@components/ui/button";
import Icon from "@components/ui/icon";
import Input from "@components/ui/input";
import Spinner from "@components/ui/spinner";
import {
  MediaLibCategoryEnum,
  MediaLibWebsiteCopyrightDialog,
  sendMediaLibUploadErrorToast,
} from "@editor/modules/media-lib";
import { isValidURL } from "@editor/utils";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { toast } from "react-toastify";

interface IProps {
  onUploadStart?: () => void;
  setCategory: (value: MediaLibCategoryEnum) => void;
  setSelectedFolderId: (value: string | null) => void;

  isImportingWebsite: boolean;
  setIsImportingWebsite: (value: boolean) => void;
  websiteUrl: string | null;
  setWebsiteUrl: (value: string | null) => void;
  controller: AbortController;
}

export const MediaLibUploadWebsite = ({
  onUploadStart,
  setCategory,
  setSelectedFolderId,
  isImportingWebsite,
  setIsImportingWebsite,
  websiteUrl,
  setWebsiteUrl,
  controller,
}: IProps) => {
  const dispatch = useAppDispatch();

  const { refetch } = useGetMediaFoldersQuery({ archived: false });
  const [extractWebsiteMedia] = useExtractWebsiteMediaMutation();

  const handleImport = () => {
    if (
      isImportingWebsite ||
      websiteUrl?.length === 0 ||
      !isValidURL(websiteUrl ?? "")
    ) {
      return;
    }
    dispatch(
      openDialog({
        id: MediaLibWebsiteCopyrightDialog.dialogId,
        content: MediaLibWebsiteCopyrightDialog,
        props: {
          setIsImportingWebsite,
          setWebsiteUrl,
          websiteUrl,
          onSubmit: async () => {
            if (!websiteUrl) return;
            if (onUploadStart) onUploadStart();
            try {
              let finalUrl = websiteUrl;
              if (websiteUrl.endsWith("/")) {
                finalUrl = websiteUrl.slice(0, -1);
              }

              const data = await extractWebsiteMedia({
                url: finalUrl,
                signal: controller.signal,
              }).unwrap();

              if (data.files.length === 0) {
                toast.warning(
                  "We couldn’t find anything to import from your website.",
                  { autoClose: 7 * 1000 },
                );
                return;
              }

              if (data.folder) {
                setCategory(MediaLibCategoryEnum.Website);
                await refetch();
                setSelectedFolderId(data.folder._id);
              }
            } catch (e) {
              console.log(e);
              if (!controller.signal.aborted) {
                track("error extracting media from website", {
                  error: e,
                  url: websiteUrl,
                });
                toast.error(<ErrorToast />, { autoClose: 5000 });
              } else {
                sendMediaLibUploadErrorToast({
                  title: "Media extraction canceled",
                });
              }
            }
            setWebsiteUrl(null);
            setIsImportingWebsite(false);
          },
        },
      }),
    );
  };

  return (
    <div className="tw-grid tw-grid-cols-[1fr_auto] tw-items-center tw-gap-2">
      <div className="tw-relative">
        {isImportingWebsite && (
          <div className="tw-absolute tw-right-2 tw-top-1/2 tw-z-10 -tw-translate-y-1/2">
            <Spinner />
          </div>
        )}

        <Input
          placeholder="https://"
          autoFocus
          className={cn(
            "tw-h-9 tw-w-full tw-pl-2",
            isImportingWebsite ? "tw-pr-8" : "tw-pr-2",
          )}
          value={websiteUrl ?? ""}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleImport();
            }
          }}
          onChange={
            isImportingWebsite
              ? undefined
              : (e) => setWebsiteUrl(e.target.value)
          }
        />
      </div>
      <Button
        variant="secondary"
        size="sm"
        disabled={websiteUrl?.length === 0 || !isValidURL(websiteUrl ?? "")}
        className={cn(
          "tw-h-9 tw-w-[100px]",
          isImportingWebsite && "tw-cursor-wait",
        )}
        onClick={handleImport}
      >
        {isImportingWebsite ? "Importing..." : "Import"}
      </Button>
      {isImportingWebsite && (
        <div className="tw-col-span-2 tw-flex tw-items-center tw-rounded tw-border tw-border-blue-100 tw-bg-blue-50 tw-px-3 tw-py-2">
          <Icon
            icon="ri-information-fill"
            className="tw-mr-2 tw-text-2xl tw-text-blue-500"
          />
          <span className="tw-text-[11px]/none">It might take a while.</span>
        </div>
      )}
    </div>
  );
};

const ErrorToast = () => {
  return (
    <div className="tw-flex tw-items-center tw-justify-between">
      <div className="tw-mr-6 tw-flex tw-flex-col tw-text-left">
        <span className="tw-text-[13px]/none tw-font-semibold tw-text-white">
          We couldn’t import your media
        </span>
        <span className="tw-text-[11px] tw-font-normal tw-text-white/80">
          It might be too large or unavailable
        </span>
      </div>
      <Button
        variant="secondary"
        size="xs"
        onClick={() => {
          window.Intercom?.("show");
        }}
      >
        Contact support
      </Button>
    </div>
  );
};
